import { useEffect, useRef, useState } from 'react'
import { detectDocument, loadWorkImage, scanReceipt, type Quad } from '../lib/receiptScanner'

type Step = 'pick' | 'adjust' | 'done'

/**
 * Tag et billede af kvitteringen, og få den scannet (beslutning 114): systemet finder papiret og klipper det ud med
 * det samme, når det er sikkert på kanterne. Er det ikke, vises hjørnerne, så man kan trække dem på plads - og det
 * kan man også bagefter med "Ret hjørnerne". Svarer med den færdige JPEG.
 */
export function ReceiptScanner({ onScanned }: { onScanned: (blob: Blob | null) => void }) {
  const [step, setStep] = useState<Step>('pick')
  const [source, setSource] = useState<HTMLCanvasElement | null>(null)
  const [quad, setQuad] = useState<Quad | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [unsure, setUnsure] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => () => {
    if (preview) URL.revokeObjectURL(preview)
  }, [preview])

  async function pick(file: File | undefined) {
    if (!file) return
    setError(null)
    setBusy(true)
    try {
      const canvas = await loadWorkImage(file)
      const detection = detectDocument(canvas)
      setSource(canvas)
      setQuad(detection.quad)
      setUnsure(!detection.confident)
      onScanned(null)
      if (detection.confident) {
        await finish(canvas, detection.quad)
      } else {
        setStep('adjust')
      }
    } catch {
      setError('Billedet kunne ikke åbnes. Prøv at tage det igen.')
    } finally {
      setBusy(false)
    }
  }

  async function finish(from = source, corners = quad) {
    if (!from || !corners) return
    setBusy(true)
    try {
      const blob = await scanReceipt(from, corners)
      if (preview) URL.revokeObjectURL(preview)
      setPreview(URL.createObjectURL(blob))
      setStep('done')
      onScanned(blob)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kvitteringen kunne ikke scannes.')
    } finally {
      setBusy(false)
    }
  }

  function retake() {
    setStep('pick')
    setSource(null)
    setQuad(null)
    onScanned(null)
  }

  return (
    <div className="stack" style={{ gap: 10 }}>
      {step === 'pick' && (
        <label className="btn btn--primary" style={{ cursor: 'pointer', justifyContent: 'center' }}>
          {busy ? 'Finder kvitteringen ...' : '📷 Tag billede af kvitteringen'}
          <input
            type="file"
            accept="image/*"
            capture="environment"
            style={{ display: 'none' }}
            onChange={(e) => {
              void pick(e.target.files?.[0])
              e.target.value = ''
            }}
          />
        </label>
      )}

      {step === 'pick' && (
        <div className="tiny muted">
          Læg kvitteringen på et bord, og hold telefonen lige over den, så hele kvitteringen er med. Systemet finder
          selv kanterne og klipper den ud - det går bedst på et mørkt bord.
        </div>
      )}

      {step === 'adjust' && source && quad && (
        <>
          <div className="small">
            {unsure && 'Kanterne kunne ikke findes sikkert. '}Træk i de fire prikker, så de sidder i kvitteringens
            hjørner.
          </div>
          <CornerEditor source={source} quad={quad} onChange={setQuad} />
          <div className="btn-group">
            <button type="button" className="btn btn--primary" disabled={busy} onClick={() => void finish()}>
              {busy ? 'Scanner ...' : 'Brug kvitteringen'}
            </button>
            <button type="button" className="btn" onClick={retake}>
              Tag et nyt billede
            </button>
          </div>
        </>
      )}

      {step === 'done' && preview && (
        <>
          <img
            src={preview}
            alt="Den scannede kvittering"
            style={{ maxWidth: '100%', maxHeight: 420, objectFit: 'contain', border: '1px solid var(--border)', borderRadius: 8, alignSelf: 'flex-start' }}
          />
          <div className="tiny muted">Er der klippet forkert, så tryk "Ret hjørnerne".</div>
          <div className="btn-group">
            <button
              type="button"
              className="btn btn--sm"
              onClick={() => {
                setUnsure(false)
                setStep('adjust')
              }}
            >
              Ret hjørnerne
            </button>
            <button type="button" className="btn btn--sm" onClick={retake}>
              Tag et nyt billede
            </button>
          </div>
        </>
      )}

      {error && <div className="error-box">{error}</div>}
    </div>
  )
}

/** Billedet med papirets omrids og fire prikker, man kan trække i. */
function CornerEditor({
  source,
  quad,
  onChange
}: {
  source: HTMLCanvasElement
  quad: Quad
  onChange: (quad: Quad) => void
}) {
  const box = useRef<HTMLDivElement>(null)
  const canvas = useRef<HTMLCanvasElement>(null)
  const [dragging, setDragging] = useState<number | null>(null)

  useEffect(() => {
    const target = canvas.current
    if (!target) return
    target.width = source.width
    target.height = source.height
    target.getContext('2d')!.drawImage(source, 0, 0)
  }, [source])

  function toImage(event: React.PointerEvent) {
    const rect = box.current!.getBoundingClientRect()
    const x = ((event.clientX - rect.left) / rect.width) * source.width
    const y = ((event.clientY - rect.top) / rect.height) * source.height
    return {
      x: Math.max(0, Math.min(source.width, x)),
      y: Math.max(0, Math.min(source.height, y))
    }
  }

  const handle = Math.max(source.width, source.height) * 0.022
  const points = quad.map((p) => `${p.x},${p.y}`).join(' ')

  return (
    <div
      ref={box}
      style={{ position: 'relative', width: '100%', maxWidth: 520, touchAction: 'none', userSelect: 'none' }}
      onPointerMove={(event) => {
        if (dragging === null) return
        const next = [...quad] as Quad
        next[dragging] = toImage(event)
        onChange(next)
      }}
      onPointerUp={() => setDragging(null)}
      onPointerLeave={() => setDragging(null)}
    >
      <canvas ref={canvas} style={{ width: '100%', display: 'block', borderRadius: 8 }} />
      <svg
        viewBox={`0 0 ${source.width} ${source.height}`}
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
      >
        <polygon points={points} fill="rgba(46, 42, 133, 0.18)" stroke="#2E2A85" strokeWidth={handle * 0.3} />
        {quad.map((p, i) => (
          <circle
            key={i}
            cx={p.x}
            cy={p.y}
            r={handle}
            fill="#fff"
            stroke="#2E2A85"
            strokeWidth={handle * 0.35}
            style={{ cursor: 'grab' }}
            onPointerDown={(event) => {
              event.preventDefault()
              ;(event.target as Element).setPointerCapture?.(event.pointerId)
              setDragging(i)
            }}
          />
        ))}
      </svg>
    </div>
  )
}
